import React, { useEffect } from 'react';
import { BrowserRouter as Router, Redirect, Route } from 'react-router-dom';
import { AppInsightsContext, AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { SnackbarProvider } from 'notistack';
import { reactPlugin } from './services/appInsights';
import './App.css';
import Default from './components/MainMenu/index';
import AlumniListTable from './components/AlumniList/AlumniListTable';
import SeniorListTable from './components/SeniorList/SeniorListTable';
import FamilyHomeListTable from './components/SeniorList/FamilyHomeListTable';
import SeniorPlanner from './components/SeniorList/SeniorPlanner';
import Unauthorized from './components/Unauthorized/index';
import Layout from './components/Shared/Layout';
import AccessRequest from './components/AccessRequest/index';
import AccessRequestList from './components/AccessRequest/accessRequestList';
import AftercareApplicationsList from './components/AftercareApplications/AftercareApplicationsList';
import AftercareDisposition from './components/Aftercare/AftercareDisposition';
import ScholarshipApplicationList from './components/ScholarshipApplications/ScholarshipApplicationList';
import HelpApplicationList from './components/HelpApplications/HelpApplicationList';
import ContactList from './components/Contact/ContactList';
import ContactListMainMenu from './components/Contact/ContactListMainMenu';
import Contact from './components/Contact/Contact';
import ScholarshipDisposition from './components/Scholarship/ScholarshipDisposition';
import HelpRequestDisposition from './components/HelpApplications/HelpRequestDisposition';
import Homepage from './components/Pages/Homepage';
import HowCanWeHelp from './components/Pages/HowCanWeHelp';
import AlumniAssociation from './components/Pages/AlumniAssociation';
import Housing from './components/Pages/Housing';
import Tradelife from './components/Pages/Tradelife';
import Scholarships from './components/Pages/Scholarships';
import AboutUs from './components/Pages/AboutUs';
import ReportList from './components/Reports/ReportList';
import Recommendation from './components/Pages/Recommendation';
import AwardPaymentRequestsSummary from './components/Pages/AwardPaymentRequestsSummary';
import PrivateRoute from './PrivateRoute';
import { inject, observer } from 'mobx-react';
import FileHostingLanding from './components/digitization';
import StaffTable from './components/Admin/Staff/StaffTable';
import PATH from './constants/paths';
import Resources from './components/digitization/Resources';
import AwardPaymentRequest from './components/AwardPayment/AwardPaymentRequest';
import RequestResponse from './components/AwardPayment/RequestResponse';

const App = inject('SeniorStore')(
  observer(({ SeniorStore: seniorStore }) => {
    useEffect(() => {
      return () => {
        seniorStore.removeFromSession();
      };
    }, []);

    return (
      <div className='App'>
        <AppInsightsContext.Provider value={reactPlugin}>
          <AppInsightsErrorBoundary
            onError={() => (
              <Router>
                <Layout>
                  <h2>Something unexpected has happened. Please try again.</h2>
                </Layout>
              </Router>
            )}
            appInsights={reactPlugin}
          >
            <Router>
              <Layout>
                <SnackbarProvider maxSnack={3}>
                  <Route path='/home' component={Homepage} />
                  <Route path='/help' component={HowCanWeHelp} />
                  <Route path='/alumni' component={AlumniAssociation} />
                  <Route path='/housing' component={Housing} />
                  <Route path='/tradelife' component={Tradelife} />
                  <Route path='/scholarships' component={Scholarships} />
                  <Route path='/about' component={AboutUs} />
                  <Route path='/requestaccess' component={AccessRequest} />
                  <Route path='/recommendation/:token?' component={Recommendation} />
                  <Route path={PATH.RESOURCES} component={Resources} />
                  <PrivateRoute path='/' exact>
                    <Default />
                  </PrivateRoute>
                  <PrivateRoute path='/alumnilist'>
                    <AlumniListTable />
                  </PrivateRoute>
                  <PrivateRoute path='/seniorlist'>
                    <SeniorListTable />
                  </PrivateRoute>
                  <PrivateRoute path='/familyhomelist'>
                    <FamilyHomeListTable />
                  </PrivateRoute>
                  <PrivateRoute path='/seniorplanner'>
                    <SeniorPlanner />
                  </PrivateRoute>
                  <PrivateRoute path='/accessrequestlist'>
                    <AccessRequestList />
                  </PrivateRoute>
                  <PrivateRoute path='/aftercarelist'>
                    <AftercareApplicationsList />
                  </PrivateRoute>
                  <PrivateRoute path='/aftercareapp'>
                    <AftercareDisposition />
                  </PrivateRoute>
                  <PrivateRoute path='/scholarshiplist'>
                    <ScholarshipApplicationList />
                  </PrivateRoute>
                  <PrivateRoute path='/helprequestlist'>
                    <HelpApplicationList />
                  </PrivateRoute>
                  <PrivateRoute path='/helprequest'>
                    <HelpRequestDisposition />
                  </PrivateRoute>
                  <PrivateRoute path='/scholarshipapp'>
                    <ScholarshipDisposition />
                  </PrivateRoute>
                  <PrivateRoute path='/reports'>
                    <ReportList />
                  </PrivateRoute>
                  <PrivateRoute path='/contactlist'>
                    <ContactList />
                  </PrivateRoute>
                  <PrivateRoute path='/contactlistmainmenu'>
                    <ContactListMainMenu />
                  </PrivateRoute>
                  <PrivateRoute path='/contact'>
                    <Contact />
                  </PrivateRoute>
                  <PrivateRoute path={PATH.FILE_HOSTING}>
                    <FileHostingLanding />
                  </PrivateRoute>
                  <PrivateRoute path='/staffadmin'>
                    <StaffTable />
                  </PrivateRoute>
                  <PrivateRoute path={PATH.AWARD_PAYMENT_REQUESTS}>
                    <AwardPaymentRequestsSummary />
                  </PrivateRoute>
                  <PrivateRoute path={PATH.AWARD_PAYMENT_REQUEST}>
                    <AwardPaymentRequest />
                  </PrivateRoute>
                  <PrivateRoute path={PATH.AWARD_PAYMENT_RESPONSE}>
                    <RequestResponse />
                  </PrivateRoute>
                  <Route path='/default' render={() => <Redirect to='/' />} />
                  <Route path='/unauthorized' component={Unauthorized} />
                </SnackbarProvider>
              </Layout>
            </Router>
          </AppInsightsErrorBoundary>
        </AppInsightsContext.Provider>
      </div>
    );
  })
);

export default App;
